import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { motion } from 'framer-motion';
import { produce } from 'immer';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useActionCable } from '@/hooks/useActionCable';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useChannel } from '@/hooks/useChannel';
import useSegment from '@/hooks/useSegment';
import { setEduChatbotSessionId } from '@/redux/reducers/webAppSlice';
import {
  useAskEducationBotMutation,
  useLazyGetEducationBotHistoryQuery,
  useSendEducationBotFeedbackMutation,
} from '@/patient-app-common/api/educationApi';
import { useUpdatePatientMutation } from '@/patient-app-common/api/patientApi';
import { uuidv4 } from '@/patient-app-common/utils';
import { Education } from '@/patient-app-common/types/Education';

import { ChatbotContent } from './layout/ChatbotContent';

const SUBMIT_EVENT = 'Education AI question submitted';

const AI_FEEDBACK_HELPFUL_EVENT = 'Education AI feedback helpful';
const AI_FEEDBACK_NOT_HELPFUL_EVENT = 'Education AI feedback not helpful';
const AI_FEEDBACK_NOT_HELPFUL_WITH_COMMENT_EVENT =
  'Education AI feedback not helpful with comment';

const AI_RESPONSE_RECEIVED = 'Education AI response received';
const AI_RESPONSE_ERROR = 'Education AI error';

const AI_CONSENT_GIVEN_EVENT = 'Education AI consent given';
const AI_CONSENT_DECLINED_EVENT = 'Education AI consent declined';

type ChatbotChunk = { order: number; content: string };
type ChatbotResponseEntry = {
  id: string;
  content: string;
  chunks: ChatbotChunk[];
  queryOrder?: number;
  page?: number;
  complete?: boolean;
  error?: boolean;
  hidden?: boolean;
};

type ChunkFeedback = { helpful: boolean | null; comment: string | null };

function parseChunkFeedback(feedback: unknown): ChunkFeedback | undefined {
  if (!feedback || typeof feedback !== 'object') return;
  const fb = feedback as Record<string, unknown>;
  const helpful = fb.helpful;
  const comment = fb.comment;

  if (
    (helpful === null || typeof helpful === 'boolean') &&
    (comment === null || typeof comment === 'string')
  ) {
    return {
      helpful: helpful as boolean | null,
      comment: comment as string | null,
    };
  }
}

function formatLinks(html: string): string {
  return html.replace(/href="(https:\/\/[^"]+)"/g, (match, url) => {
    try {
      const parsedUrl = new URL(url);
      const relativePath =
        parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
      return `href="${relativePath}"`;
    } catch {
      return match;
    }
  });
}

export function EduChatbot({
  hasAiConsent,
  education,
  setSidebarOverlaps,
  pageWidth,
}: {
  hasAiConsent: boolean;
  education: Education[];
  setSidebarOverlaps: (boolean: boolean) => void;
  pageWidth: number;
}) {
  const { t } = useTranslation('education');

  const { sendTrackEvent } = useSegment();

  const { route } = useAppSelector(({ app }) => app);
  const { token } = useAppSelector(({ auth }) => auth);
  const sessionId =
    useAppSelector(({ webApp }) => webApp.eduChatbotSessionId) || '';
  const dispatch = useAppDispatch();

  console.log('session id', sessionId);

  // In dev mode, use local proxy to avoid server blocking WebSocket from localhost
  const wssRoute = import.meta.env.DEV
    ? `ws://${window.location.host}`
    : route
      ? route.replace('https://', 'wss://').replace('http://', 'ws://')
      : 'ws://0.0.0.0:3000';

  const wsUrl = `${wssRoute}/cable?token=${token}`;

  const { actionCable } = useActionCable(wsUrl);
  const { subscribe, unsubscribe } = useChannel(actionCable);

  const [postEdubot, { isLoading, isError }] = useAskEducationBotMutation();
  const [patchFeedback] = useSendEducationBotFeedbackMutation();
  const [triggerHistory] = useLazyGetEducationBotHistoryQuery();

  const [feedback, setFeedback] = useState<{
    [id: string]: {
      helpful: boolean | null;
      comment: string | null;
    };
  }>({});

  const [query, setQuery] = useState('');
  const [consentModal, toggleConsentModal] = useState(false);

  const [responseHistory, setResponseHistory] = useState<
    ChatbotResponseEntry[]
  >([]);

  const isMobileChatbot = pageWidth <= 767;

  const queryRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestedHistoryPagesRef = useRef<Set<number>>(new Set());
  const activePageRef = useRef<number | null>(null);
  const scrollRestoreRef = useRef<{
    prevHeight: number;
    prevTop: number;
  } | null>(null);
  const suppressScrollRef = useRef(false);
  const initialScrollDoneRef = useRef(false);
  const [currentHistoryPage, setCurrentHistoryPage] = useState(1);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [isCableConnected, setIsCableConnected] = useState(false);
  const [historyStatus, setHistoryStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');

  const isNewSessionRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      isNewSessionRef.current = true;
      const newSessionId = uuidv4();
      dispatch(setEduChatbotSessionId(newSessionId));
    } else {
      const isNew = isNewSessionRef.current;
      isNewSessionRef.current = false;

      requestedHistoryPagesRef.current = new Set();
      setCurrentHistoryPage(1);
      setIsLoadingMoreHistory(false);
      initialScrollDoneRef.current = false;
      setResponseHistory([]);
      setFeedback({});

      if (isNew) {
        requestedHistoryPagesRef.current.add(1);
        setHasMoreHistory(false);
        setHistoryStatus('loaded');
      } else {
        setHasMoreHistory(true);
        setHistoryStatus('loading');
      }
    }
  }, [dispatch, sessionId]);

  const isCableConnectedRef = useRef(false);
  isCableConnectedRef.current = isCableConnected;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const requestHistoryIfNeeded = useCallback(() => {
    if (!isCableConnectedRef.current) return;
    if (!sessionIdRef.current) return;
    if (requestedHistoryPagesRef.current.has(1)) return;

    setHistoryStatus('loading');
    activePageRef.current = 1;
    requestedHistoryPagesRef.current.add(1);

    triggerHistory({ chatId: sessionIdRef.current, page: 1 })
      .unwrap()
      .catch((error) => {
        console.error('[EduChatbot] Error requesting history:', error);
        setHistoryStatus('error');
        requestedHistoryPagesRef.current.delete(1);
      });
  }, [triggerHistory]);

  useEffect(() => {
    if (isCableConnected && sessionId) {
      requestHistoryIfNeeded();
    }
  }, [isCableConnected, sessionId, requestHistoryIfNeeded]);

  const loadMoreHistory = useCallback(() => {
    if (!isCableConnected) return;
    if (!sessionId) return;
    if (!hasMoreHistory) return;
    if (isLoadingMoreHistory) return;

    const nextPage = currentHistoryPage + 1;
    if (requestedHistoryPagesRef.current.has(nextPage)) return;

    const container = containerRef.current;
    if (container) {
      scrollRestoreRef.current = {
        prevHeight: container.scrollHeight,
        prevTop: container.scrollTop,
      };
    }

    activePageRef.current = nextPage;
    requestedHistoryPagesRef.current.add(nextPage);
    setIsLoadingMoreHistory(true);

    triggerHistory({ chatId: sessionId, page: nextPage })
      .unwrap()
      .then(() => {
        setCurrentHistoryPage(nextPage);
      })
      .catch((error) => {
        console.error(
          '[EduChatbot] Error requesting additional history:',
          error
        );
        requestedHistoryPagesRef.current.delete(nextPage);
      })
      .finally(() => {
        setIsLoadingMoreHistory(false);
      });
  }, [
    currentHistoryPage,
    hasMoreHistory,
    isCableConnected,
    isLoadingMoreHistory,
    sessionId,
    triggerHistory,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (suppressScrollRef.current) return;
      if (historyStatus !== 'loaded') return;
      if (container.scrollTop <= 50) {
        loadMoreHistory();
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreHistory, historyStatus]);

  // When we prepend older history (load more pages), keep the user's
  // visible messages in place instead of jumping to the top of the page.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const restore = scrollRestoreRef.current;
    if (!restore || isLoadingMoreHistory) return;

    const addedHeight = container.scrollHeight - restore.prevHeight;
    suppressScrollRef.current = true;
    container.scrollTop = restore.prevTop + addedHeight;
    scrollRestoreRef.current = null;
    setTimeout(() => {
      suppressScrollRef.current = false;
    }, 0);
  }, [responseHistory, isLoadingMoreHistory]);

  const scrollToQuestion = useCallback(() => {
    const scroll = () => {
      const questionElement = queryRef.current;
      const container = containerRef.current;
      if (!questionElement || !container) return;

      const containerRect = container.getBoundingClientRect();
      const questionRect = questionElement.getBoundingClientRect();
      const relativeTop =
        questionRect.top - containerRect.top + container.scrollTop;

      suppressScrollRef.current = true;
      container.scrollTo({
        top: relativeTop,
        behavior: 'smooth',
      });
      setTimeout(() => {
        suppressScrollRef.current = false;
      }, 600);
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(scroll);
    });
  }, []);

  useEffect(() => {
    if (isLoading) {
      scrollToQuestion();
    }
  }, [isLoading, responseHistory, scrollToQuestion]);

  const addChunk = useCallback(
    (chunkData: {
      id: string;
      order: number;
      queryOrder?: number;
      page?: number;
      content?: string;
      feedback?: { helpful: boolean | null; comment: string | null };
      complete?: boolean;
    }) => {
      if (chunkData.feedback) {
        const fb = chunkData.feedback;
        setFeedback((prev) => ({
          ...prev,
          [chunkData.id]: {
            helpful: fb.helpful ?? null,
            comment: fb.comment || null,
          },
        }));
      }

      // Some chunks may only carry feedback/metadata
      if (!chunkData.content) return;

      setResponseHistory(
        produce((draft: ChatbotResponseEntry[]) => {
          const existingEntry = draft.find(
            (entry) => entry.id === chunkData.id
          );

          let needsSort = false;

          if (existingEntry) {
            if (chunkData.complete !== undefined) {
              existingEntry.complete = chunkData.complete;
            }

            if (
              typeof chunkData.queryOrder === 'number' &&
              typeof existingEntry.queryOrder !== 'number'
            ) {
              existingEntry.queryOrder = chunkData.queryOrder;
              needsSort = true;
            }

            if (
              typeof chunkData.page === 'number' &&
              typeof existingEntry.page !== 'number'
            ) {
              existingEntry.page = chunkData.page;
              needsSort = true;
            }

            if (!Array.isArray(existingEntry.chunks)) {
              existingEntry.chunks = [];
            }

            const alreadyReceived = existingEntry.chunks.some(
              (chunk) => chunk.order === chunkData.order
            );

            if (!alreadyReceived) {
              existingEntry.chunks.push({
                order: chunkData.order,
                content: formatLinks(chunkData.content!),
              });

              existingEntry.chunks.sort((a, b) => a.order - b.order);

              existingEntry.content = existingEntry.chunks
                .map((chunk) => chunk.content)
                .join('');

              if (existingEntry.chunks.length > 1) {
                existingEntry.error = false;
                existingEntry.hidden = false;
              }
            }
          } else {
            needsSort = true;
            draft.push({
              id: chunkData.id,
              content: formatLinks(chunkData.content!),
              chunks: [
                {
                  order: chunkData.order,
                  content: formatLinks(chunkData.content!),
                },
              ],
              queryOrder: chunkData.queryOrder,
              page: chunkData.page,
              complete: chunkData.complete ?? false,
              error: false,
              hidden: false,
            });
          }

          if (needsSort) {
            draft.sort((a, b) => {
              // 1) Sort by page number DESC (older pages have higher page numbers)
              const pageA = typeof a.page === 'number' ? a.page : -1;
              const pageB = typeof b.page === 'number' ? b.page : -1;
              if (pageA !== pageB) {
                return pageB - pageA;
              }

              // 2) Then by queryOrder ASC
              const qoA =
                typeof a.queryOrder === 'number'
                  ? a.queryOrder
                  : Number.MAX_SAFE_INTEGER;
              const qoB =
                typeof b.queryOrder === 'number'
                  ? b.queryOrder
                  : Number.MAX_SAFE_INTEGER;
              if (qoA !== qoB) {
                return qoA - qoB;
              }

              return 0;
            });
          }
        })
      );
    },
    []
  );

  const handleReceived = useCallback(
    (data: unknown) => {
      if (!data || typeof data !== 'object') return;
      const payload = data as Record<string, unknown>;
      const type = payload.type;

      if (type === 'history_chunk') {
        const id = payload.id;
        const order = payload.order;
        const content = payload.content;
        const complete = payload.complete;
        const queryOrderRaw = payload['query_order'];
        const queryOrder =
          typeof queryOrderRaw === 'number' ? queryOrderRaw : undefined;
        const pageRaw = payload['page'];
        const page =
          typeof pageRaw === 'number'
            ? pageRaw
            : typeof activePageRef.current === 'number'
              ? activePageRef.current
              : undefined;

        if (typeof id !== 'string') return;
        if (typeof order !== 'number') return;
        if (typeof content !== 'string') return;

        // feedback is only included on the last chunk for that id
        const fb = parseChunkFeedback(payload.feedback);

        addChunk({
          id,
          order,
          queryOrder,
          page,
          content,
          feedback: fb,
          complete: typeof complete === 'boolean' ? complete : undefined,
        });
        return;
      }

      if (type === 'history_complete') {
        const pageRaw = payload['page'];
        const potentiallyMoreLogsRaw = payload['potentially_more_logs'];
        const page =
          typeof pageRaw === 'number'
            ? pageRaw
            : typeof activePageRef.current === 'number'
              ? activePageRef.current
              : undefined;

        if (potentiallyMoreLogsRaw === false) {
          setHasMoreHistory(false);
        }

        if ((!page || page === 1) && !initialScrollDoneRef.current) {
          scrollToQuestion();
          initialScrollDoneRef.current = true;
        }

        setHistoryStatus('loaded');
        return;
      }

      if (type === 'complete') {
        const id = payload.id;
        if (typeof id !== 'string') return;
        setResponseHistory((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, complete: true } : entry
          )
        );
        sendTrackEvent(AI_RESPONSE_RECEIVED);
        scrollToQuestion();
        return;
      }

      if (type === 'reset') {
        const id = payload.id;
        if (typeof id !== 'string') return;
        setResponseHistory((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, complete: false } : entry
          )
        );
        scrollToQuestion();
        return;
      }

      if (type === 'error') {
        const id = payload.id;
        if (typeof id !== 'string') return;
        setResponseHistory((prev) =>
          prev.map((entry) =>
            entry.id === id ? { ...entry, error: true } : entry
          )
        );
        sendTrackEvent(AI_RESPONSE_ERROR);
        scrollToQuestion();
        return;
      }

      // live chunk: expect at least {id, order, content}
      const id = payload.id;
      const order = payload.order;
      const content = payload.content;
      if (typeof id !== 'string') return;
      if (typeof order !== 'number') return;
      if (typeof content !== 'string') return;
      addChunk({ id, order, content });
      setResponseHistory((prev) =>
        prev.map((entry) => (entry.error ? { ...entry, hidden: true } : entry))
      );
    },
    [addChunk, sendTrackEvent, scrollToQuestion]
  );

  const handleReceivedRef = useRef(handleReceived);
  handleReceivedRef.current = handleReceived;
  const subscribeRef = useRef(subscribe);
  subscribeRef.current = subscribe;
  const unsubscribeRef = useRef(unsubscribe);
  unsubscribeRef.current = unsubscribe;

  useEffect(() => {
    if (!actionCable) {
      console.warn('[EduChatbot] Cannot subscribe: actionCable is null');
      return;
    }

    subscribeRef.current(
      {
        channel: 'AskSeamlessChannel',
      },
      {
        received: (data: unknown) => {
          handleReceivedRef.current(data);
        },
        connected: () => {
          isCableConnectedRef.current = true;
          setIsCableConnected(true);
        },
        disconnected: () => {
          isCableConnectedRef.current = false;
          setIsCableConnected(false);
        },
        rejected: () => {
          console.error(
            '[EduChatbot] Subscription to AskSeamlessChannel was rejected'
          );
        },
      }
    );
    return () => {
      unsubscribeRef.current();
    };
  }, [actionCable]);

  const handleSubmit = (queryToSend = query) => {
    if (!queryToSend) return;

    if (!hasAiConsent) {
      toggleConsentModal(true);
      return;
    }
    submitQuery(queryToSend);
  };

  const submitQuery = async (queryToSend = query) => {
    const params = {
      chatId: sessionId,
      query: queryToSend,
    };

    sendTrackEvent(SUBMIT_EVENT);

    try {
      const result = await postEdubot(params).unwrap();

      if (result.success) {
        setQuery('');
      }
    } catch (e) {
      console.error('[EduChatbot] API error:', e);
    }
  };

  const submitFeedback = (id: string, helpful: boolean, comment?: string) => {
    setFeedback((prev) => ({
      ...prev,
      [id]: {
        helpful,
        comment: comment || null,
      },
    }));
    patchFeedback({
      id,
      helpful,
      ...(comment && { comment }),
    });
    if (helpful) {
      sendTrackEvent(AI_FEEDBACK_HELPFUL_EVENT);
    } else if (comment) {
      sendTrackEvent(AI_FEEDBACK_NOT_HELPFUL_WITH_COMMENT_EVENT);
    } else {
      sendTrackEvent(AI_FEEDBACK_NOT_HELPFUL_EVENT);
    }
  };

  const isChatSendDisabled = isLoading || historyStatus === 'loading';

  const chatbotContent = (
    <ChatbotContent
      containerRef={containerRef}
      handleSubmit={handleSubmit}
      submitFeedback={submitFeedback}
      setQuery={setQuery}
      queryRef={queryRef}
      query={query}
      isLoading={isLoading}
      isHistoryLoading={historyStatus === 'loading'}
      isError={isError}
      hasAiConsent={hasAiConsent}
      isChatSendDisabled={isChatSendDisabled}
      responseHistory={responseHistory}
      education={education}
      pageWidth={pageWidth}
      feedback={feedback}
      isLoadingMoreHistory={isLoadingMoreHistory}
    />
  );

  return (
    <>
      <ConsentModal
        callback={submitQuery}
        modal={consentModal}
        toggleModal={toggleConsentModal}
      />
      {isMobileChatbot ? (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          className="fixed inset-0 z-50 bg-background flex flex-col"
        >
          {/* Header for the full-screened drawer */}
          <div className="flex-shrink-0 bg-background border-b border-default-200 p-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                {t('common.link.answers')}
              </h2>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                onPress={() => {
                  setSidebarOverlaps(true);
                }}
                aria-label={t('common.action.close')}
              >
                <Icon icon="mdi:close" className="text-xl" />
              </Button>
            </div>
          </div>
          {chatbotContent}
        </motion.div>
      ) : (
        chatbotContent
      )}
    </>
  );
}

function ConsentModal({
  callback,
  modal,
  toggleModal,
}: {
  callback: () => void;
  modal: boolean;
  toggleModal: (toggle: boolean) => void;
}) {
  const { sendTrackEvent } = useSegment();
  const { t } = useTranslation('education');
  const [updatePatient, { isLoading }] = useUpdatePatientMutation();

  const [error, setError] = useState('');

  const handleSubmit = async () => {
    try {
      const result = await updatePatient({ hasAiConsent: true }).unwrap();

      if (result.success) {
        toggleModal(false);
        callback();
        sendTrackEvent(AI_CONSENT_GIVEN_EVENT);
      } else {
        setError(t('common.error.server'));
      }
    } catch (e) {
      console.error(e);
      setError(t('common.error.server'));
    }
  };

  return (
    <Modal isOpen={modal} onClose={() => toggleModal(false)}>
      <ModalContent>
        <ModalHeader>{t('educhatbot.consent_modal.title')}</ModalHeader>
        <ModalBody>
          <Alert
            color="danger"
            description={error}
            hideIconWrapper
            isVisible={!_.isEmpty(error)}
            variant="faded"
          />
          <p>{t('educhatbot.consent_modal.description')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" isLoading={isLoading} onPress={handleSubmit}>
            {t('educhatbot.consent_modal.button_submit')}
          </Button>
          <Button
            color="primary"
            onPress={() => {
              toggleModal(false);
              sendTrackEvent(AI_CONSENT_DECLINED_EVENT);
            }}
            variant="bordered"
          >
            {t('educhatbot.consent_modal.button_cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
